'use client';

import {
  useAuth,
  useFollow,
  useGetFollowsInfinite,
  usePrefetchGetUser,
  useUnfollow,
} from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { InfiniteList } from '@/components/ui/infinite-list';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { Link } from '@/i18n/navigation';
import { getProfileHref } from '@/lib/profile/profile-href';
import { FOLLOWS_LIST_FIELDS, PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';

import { showToast } from '@/lib/ui/toast';

interface FollowersDialogProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: 'followers' | 'following';
}

/**
 * Local row type derived from `GetFollowsRow.user` — the v2 follows feed
 * carries a nested `user` object; the dialog rows project it into the
 * `<UserAvatar>` / `<UserIdentity>` shape.
 */
interface FollowRowUser {
  readonly userId: string | null;
  readonly walletAddress: string;
  readonly displayName: string | null;
  readonly handle: string | null;
  readonly avatarUrl: string | null;
  readonly avatarNsfw?: boolean;
  readonly avatarPreferences?: { readonly rasterise: boolean };
}

// ---------------------------------------------------------------------------
// Following tab item — always shows Unfollow, removes on click
// ---------------------------------------------------------------------------

function FollowingListItem({
  user,
  onNavigate,
  onUnfollow,
}: {
  user: FollowRowUser;
  onNavigate: () => void;
  onUnfollow: (userId: string) => void;
}): React.ReactNode {
  const t = useTranslations('profile.follow');
  const prefetchUser = usePrefetchGetUser();
  const [isHovered, setIsHovered] = useState(false);
  const targetId = user.userId ?? user.walletAddress;

  function prefetch(): void {
    void prefetchUser({ identifier: targetId, fields: [...PORTFOLIO_USER_PROFILE_FIELDS] });
  }

  return (
    <div className="flex w-full items-center gap-3 rounded-card px-3 py-2">
      <Link
        href={getProfileHref(targetId)}
        onClick={onNavigate}
        onMouseEnter={prefetch}
        onFocus={prefetch}
        onPointerDown={prefetch}
        className="flex min-w-0 grow items-center gap-3 text-left transition-colors hover:opacity-80"
      >
        <UserAvatar
          size="md"
          userId={targetId}
          displayName={user.displayName}
          avatarUrl={user.avatarUrl}
          nsfw={user.avatarNsfw ?? false}
          rasterise={user.avatarPreferences?.rasterise ?? false}
          className="shrink-0"
        />
        <UserIdentity
          user={{
            userId: targetId,
            displayName: user.displayName,
            handle: user.handle,
            avatarUrl: user.avatarUrl ?? null,
            avatarNsfw: user.avatarNsfw === true,
            connectedProviders: [],
          }}
          size="sm"
        />
      </Link>
      <AnimatedButton
        variant={isHovered ? 'destructive' : 'secondary'}
        size="sm"
        className="shrink-0"
        onClick={(e: React.MouseEvent): void => {
          e.stopPropagation();
          onUnfollow(targetId);
        }}
        onPointerEnter={(): void => {
          setIsHovered(true);
        }}
        onPointerLeave={(): void => {
          setIsHovered(false);
        }}
      >
        {isHovered ? t('unfollow_button') : t('following_button')}
      </AnimatedButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Followers tab item — shows Follow/Following based on whether you follow back
// ---------------------------------------------------------------------------

function FollowerListItem({
  user,
  isFollowedBack,
  onNavigate,
  onFollow,
  onUnfollow,
}: {
  user: FollowRowUser;
  isFollowedBack: boolean;
  onNavigate: () => void;
  onFollow: (userId: string) => void;
  onUnfollow: (userId: string) => void;
}): React.ReactNode {
  const t = useTranslations('profile.follow');
  const prefetchUser = usePrefetchGetUser();
  const [isHovered, setIsHovered] = useState(false);
  const targetId = user.userId ?? user.walletAddress;

  function prefetch(): void {
    void prefetchUser({ identifier: targetId, fields: [...PORTFOLIO_USER_PROFILE_FIELDS] });
  }

  function getVariant(): 'primary' | 'secondary' | 'destructive' {
    if (!isFollowedBack) return 'primary';
    if (isHovered) return 'destructive';
    return 'secondary';
  }

  function getLabel(): string {
    if (!isFollowedBack) return t('follow_button');
    if (isHovered) return t('unfollow_button');
    return t('following_button');
  }

  return (
    <div className="flex w-full items-center gap-3 rounded-card px-3 py-2">
      <Link
        href={getProfileHref(targetId)}
        onClick={onNavigate}
        onMouseEnter={prefetch}
        onFocus={prefetch}
        onPointerDown={prefetch}
        className="flex min-w-0 grow items-center gap-3 text-left transition-colors hover:opacity-80"
      >
        <UserAvatar
          size="md"
          userId={targetId}
          displayName={user.displayName}
          avatarUrl={user.avatarUrl}
          nsfw={user.avatarNsfw ?? false}
          rasterise={user.avatarPreferences?.rasterise ?? false}
          className="shrink-0"
        />
        <UserIdentity
          user={{
            userId: targetId,
            displayName: user.displayName,
            handle: user.handle,
            avatarUrl: user.avatarUrl ?? null,
            avatarNsfw: user.avatarNsfw === true,
            connectedProviders: [],
          }}
          size="sm"
        />
      </Link>
      <AnimatedButton
        variant={getVariant()}
        size="sm"
        className="shrink-0"
        onClick={(e: React.MouseEvent): void => {
          e.stopPropagation();
          if (isFollowedBack) {
            onUnfollow(targetId);
          } else {
            onFollow(targetId);
          }
        }}
        onPointerEnter={(): void => {
          setIsHovered(true);
        }}
        onPointerLeave={(): void => {
          setIsHovered(false);
        }}
      >
        {getLabel()}
      </AnimatedButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton list
// ---------------------------------------------------------------------------

function FollowListSkeleton({ keyPrefix }: { keyPrefix: string }): React.ReactNode {
  return (
    <div className="flex flex-col gap-2 py-2">
      {['a', 'b', 'c'].map((key) => (
        <div key={`skeleton-${keyPrefix}-${key}`} className="flex items-center gap-3 px-3 py-2">
          <Skeleton className="size-9 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Following list — with unfollow that removes from list
// ---------------------------------------------------------------------------

function FollowingList({
  userId,
  onNavigate,
}: {
  userId: string;
  onNavigate: () => void;
}): React.ReactNode {
  const t = useTranslations('profile.follow');
  const query = useGetFollowsInfinite({
    userId,
    kind: 'following',
    fields: [...FOLLOWS_LIST_FIELDS],
  });
  // The SDK subscribes to relationship membership; the raw REST cache does
  // not include optimistic follows or remove optimistic unfollows.
  const items = query.rows;
  const { isPending, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const unfollow = useUnfollow();

  function handleUnfollow(targetId: string): void {
    unfollow.mutate({ userId: targetId });
    showToast({
      id: TOAST_IDS.UNFOLLOW_ACTION,
      message: t('unfollow_success'),
      variant: 'success',
    });
  }

  return (
    <InfiniteList
      items={items}
      getItemKey={(item: { user: FollowRowUser }): string =>
        item.user.userId ?? item.user.walletAddress
      }
      renderItem={(item: { user: FollowRowUser }): React.ReactNode => (
        <FollowingListItem user={item.user} onNavigate={onNavigate} onUnfollow={handleUnfollow} />
      )}
      hasNextPage={hasNextPage === true}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isInitialLoading={isPending}
      loadingPlaceholder={<FollowListSkeleton keyPrefix="following" />}
      emptyState={
        <p className="py-8 text-center text-caption text-foreground-secondary">
          {t('empty_following')}
        </p>
      }
      listClassName="flex flex-col gap-1 py-2"
      maxHeight={320}
    />
  );
}

// ---------------------------------------------------------------------------
// Followers list — with follow-back / unfollow buttons
// ---------------------------------------------------------------------------

function FollowersList({
  userId,
  onNavigate,
}: {
  userId: string;
  onNavigate: () => void;
}): React.ReactNode {
  const t = useTranslations('profile.follow');
  const followers = useGetFollowsInfinite({
    userId,
    kind: 'followers',
    fields: [...FOLLOWS_LIST_FIELDS],
  });
  // Pull the current user's full following list so we can drive
  // follow-back / unfollow UI without imperative checkFollowing calls.
  const following = useGetFollowsInfinite({
    userId,
    kind: 'following',
    fields: [...FOLLOWS_LIST_FIELDS],
  });
  const follow = useFollow();
  const unfollow = useUnfollow();

  // Follower identities come from REST/realtime; follow-back state comes
  // from the SDK's viewer relationship membership, including pending writes.
  const items = useLiveListData<{ user: FollowRowUser }>(followers.queryKey);
  const followingRows = following.rows;
  const followedBackSet = new Set<string>(
    followingRows.map((r) => r.user.userId ?? r.user.walletAddress),
  );

  function handleFollow(targetId: string): void {
    follow.mutate({ userId: targetId });
    showToast({
      id: TOAST_IDS.FOLLOW_ACTION,
      message: t('follow_success'),
      variant: 'success',
    });
  }

  function handleUnfollow(targetId: string): void {
    unfollow.mutate({ userId: targetId });
    showToast({
      id: TOAST_IDS.UNFOLLOW_ACTION,
      message: t('unfollow_success'),
      variant: 'success',
    });
  }

  return (
    <InfiniteList
      items={items}
      getItemKey={(item: { user: FollowRowUser }): string =>
        item.user.userId ?? item.user.walletAddress
      }
      renderItem={(item: { user: FollowRowUser }): React.ReactNode => {
        const rowId = item.user.userId ?? item.user.walletAddress;
        return (
          <FollowerListItem
            user={item.user}
            isFollowedBack={followedBackSet.has(rowId)}
            onNavigate={onNavigate}
            onFollow={handleFollow}
            onUnfollow={handleUnfollow}
          />
        );
      }}
      hasNextPage={followers.hasNextPage === true}
      isFetchingNextPage={followers.isFetchingNextPage}
      fetchNextPage={followers.fetchNextPage}
      isInitialLoading={followers.isPending}
      loadingPlaceholder={<FollowListSkeleton keyPrefix="followers" />}
      emptyState={
        <p className="py-8 text-center text-caption text-foreground-secondary">
          {t('empty_followers')}
        </p>
      }
      listClassName="flex flex-col gap-1 py-2"
      maxHeight={320}
    />
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export function FollowersDialog({
  userId,
  open,
  onOpenChange,
  defaultTab = 'followers',
}: FollowersDialogProps): React.ReactNode {
  const t = useTranslations('profile.follow');
  const { isAuthenticated, user } = useAuth();
  const isOwn = isAuthenticated && user !== null && user.id === userId;
  const [activeTab, setActiveTab] = useState<'followers' | 'following'>(defaultTab);
  // The sheet stays mounted while closed, so `useState(defaultTab)` only
  // applies on first mount. Re-apply on the open edge so Followers vs
  // Following KPI clicks land on the matching tab.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setActiveTab(defaultTab);
  }

  function handleClose(): void {
    onOpenChange(false);
  }

  const tabItems: SegmentedControlItem[] = [
    { key: 'followers', label: t('followers_tab') },
    { key: 'following', label: t('following_tab') },
  ];

  return (
    <SilkSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="detached"
      size="sm"
      title={t('followers_tab')}
    >
      <div className="flex flex-col gap-3">
        <SegmentedControl
          variant="tab-surface"
          asTabs
          items={tabItems}
          active={activeTab}
          onChange={(key: string): void => setActiveTab(key as 'followers' | 'following')}
          layoutId="followers-dialog-tabs-pill"
          fullWidth
          className="rounded-pill bg-background-tertiary p-1"
        />
        <MotionAnimatedAutoHeight layoutDependencies={[activeTab]}>
          {activeTab === 'followers' ? (
            isOwn ? (
              <FollowersList userId={userId} onNavigate={handleClose} />
            ) : (
              <SimpleUserList userId={userId} type="followers" onNavigate={handleClose} />
            )
          ) : isOwn ? (
            <FollowingList userId={userId} onNavigate={handleClose} />
          ) : (
            <SimpleUserList userId={userId} type="following" onNavigate={handleClose} />
          )}
        </MotionAnimatedAutoHeight>
      </div>
    </SilkSheet>
  );
}

// ---------------------------------------------------------------------------
// Simple list (no actions) — for viewing other people's profiles
// ---------------------------------------------------------------------------

function SimpleUserList({
  userId,
  type,
  onNavigate,
}: {
  userId: string;
  type: 'followers' | 'following';
  onNavigate: () => void;
}): React.ReactNode {
  const t = useTranslations('profile.follow');
  const prefetchUser = usePrefetchGetUser();
  const query = useGetFollowsInfinite({
    userId,
    kind: type,
    fields: [...FOLLOWS_LIST_FIELDS],
  });
  // Subscribe to cache `data` so optimistic follow/unfollow patches re-render
  // (the page-level observer is silent on `data` — see sdk-list-rendering).
  const items = useLiveListData<{ user: FollowRowUser }>(query.queryKey);
  const { isPending, hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  return (
    <InfiniteList
      items={items}
      getItemKey={(item: { user: FollowRowUser }): string =>
        item.user.userId ?? item.user.walletAddress
      }
      renderItem={(item: { user: FollowRowUser }): React.ReactNode => {
        const rowId = item.user.userId ?? item.user.walletAddress;
        const prefetch = (): void => {
          void prefetchUser({ identifier: rowId, fields: [...PORTFOLIO_USER_PROFILE_FIELDS] });
        };
        return (
          <Link
            href={getProfileHref(rowId)}
            onClick={onNavigate}
            onMouseEnter={prefetch}
            onFocus={prefetch}
            onPointerDown={prefetch}
            className="flex w-full items-center gap-3 rounded-card px-3 py-2 text-left transition-colors hover:bg-background-tertiary/50"
          >
            <UserAvatar
              size="md"
              userId={rowId}
              displayName={item.user.displayName}
              avatarUrl={item.user.avatarUrl}
              nsfw={item.user.avatarNsfw ?? false}
              rasterise={item.user.avatarPreferences?.rasterise ?? false}
              className="shrink-0"
            />
            <UserIdentity
              user={{
                userId: rowId,
                displayName: item.user.displayName,
                handle: item.user.handle,
                avatarUrl: item.user.avatarUrl ?? null,
                avatarNsfw: item.user.avatarNsfw === true,
                connectedProviders: [],
              }}
              size="sm"
            />
          </Link>
        );
      }}
      hasNextPage={hasNextPage === true}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isInitialLoading={isPending}
      loadingPlaceholder={<FollowListSkeleton keyPrefix={type} />}
      emptyState={
        <p className="py-8 text-center text-caption text-foreground-secondary">
          {type === 'followers' ? t('empty_followers') : t('empty_following')}
        </p>
      }
      listClassName="flex flex-col gap-1 py-2"
      maxHeight={320}
    />
  );
}
